import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  sortFaqContent,
  sortFaqCategory,
  editFaqCategory,
  deleteFaqCategory,
  editFaqContent,
  deleteFaqContent,
  updateFaqContentStatus,
  updateFaqCategoryStatus,
  bEndFaqList,
  getFaqDetail
} from '../../api/faq/faq';
import type {
  SortFaqContentParams,
  SortFaqCategoryParams,
  EditFaqCategoryParams,
  DeleteFaqCategoryParams,
  EditFaqContentParams,
  DeleteFaqContentParams,
  UpdateFaqContentStatusParams,
  EditFaqCategoryResponse,
  DeleteFaqCategoryResponse,
  EditFaqContentResponse,
  DeleteFaqContentResponse,
  UpdateFaqContentStatusResponse,
  UpdateFaqCategoryStatusParams,
  UpdateFaqCategoryStatusResponse,
  BEndFaqListParams,
  BEndFaqListResponse,
  FaqCateContent,
  GetFaqDetailParams,
  GetFaqDetailResponse,
  FaqCategoryInfo,
  FaqContentInfo
} from '../../api/faq/types';
import i18n from '../../i18n';

/**
 * FAQ分类项接口
 */
interface FaqCategory {
  fcaId: number; // 分类ID
  content: string; // 分类内容
  cateIcon: string; // 分类图标
  langCode: string; // 语言代码
  articles?: FaqArticle[]; // 分类下的文章列表
}

/**
 * FAQ文章项接口
 */
interface FaqArticle {
  fcdId: number; // 内容ID
  content: string; // 文章内容
  isPopular: boolean; // 是否为热门
  publishStatus: number; // 发布状态[1: 发布, 2: 草稿]
  langCode: string; // 语言代码
  categoryId: number; // 所属分类ID
}

/**
 * FAQ状态接口
 */
export interface FaqState {
  categories: FaqCategory[]; // FAQ分类列表
  articles: FaqArticle[]; // FAQ文章列表
  faqContentList: FaqCateContent[]; // 后台FAQ列表数据
  pagination: { // 分页信息
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  } | null;
  currentArticle: FaqContentInfo | null; // 当前正在编辑的文章
  allCategories: FaqCategoryInfo[]; // 所有分类列表（用于编辑器选择）
  loading: boolean; // 加载状态
  error: string | null; // 错误信息
  shouldRefreshList: boolean; // 标记是否需要刷新列表
}

/**
 * 初始状态
 */
const initialState: FaqState = {
  categories: [],
  articles: [],
  faqContentList: [],
  pagination: null,
  currentArticle: null,
  allCategories: [],
  loading: false,
  error: null,
  shouldRefreshList: false
};

/**
 * 排序FAQ文章异步action
 */
export const sortFaqContentAsync = createAsyncThunk(
  'faq/sortFaqContent',
  async (params: SortFaqContentParams) => {
    try {
      const response = await sortFaqContent(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.sortContentFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 排序FAQ分类异步action
 */
export const sortFaqCategoryAsync = createAsyncThunk(
  'faq/sortFaqCategory',
  async (params: SortFaqCategoryParams) => {
    try {
      const response = await sortFaqCategory(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.sortCategoryFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 编辑FAQ分类异步action
 */
export const editFaqCategoryAsync = createAsyncThunk<
  EditFaqCategoryResponse,
  EditFaqCategoryParams
>(
  'faq/editFaqCategory',
  async (params: EditFaqCategoryParams) => {
    try {
      const response = await editFaqCategory(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.editCategoryFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 删除FAQ分类异步action
 */
export const deleteFaqCategoryAsync = createAsyncThunk<
  DeleteFaqCategoryResponse,
  DeleteFaqCategoryParams
>(
  'faq/deleteFaqCategory',
  async (params: DeleteFaqCategoryParams) => {
    try {
      const response = await deleteFaqCategory(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.deleteCategoryFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 编辑FAQ文章异步action
 */
export const editFaqContentAsync = createAsyncThunk<
  EditFaqContentResponse,
  EditFaqContentParams
>(
  'faq/editFaqContent',
  async (params: EditFaqContentParams) => {
    try {
      const response = await editFaqContent(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.editContentFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 删除FAQ文章异步action
 */
export const deleteFaqContentAsync = createAsyncThunk<
  DeleteFaqContentResponse,
  DeleteFaqContentParams
>(
  'faq/deleteFaqContent',
  async (params: DeleteFaqContentParams) => {
    try {
      const response = await deleteFaqContent(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.deleteContentFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 更新FAQ文章状态异步action
 */
export const updateFaqContentStatusAsync = createAsyncThunk<
  UpdateFaqContentStatusResponse,
  UpdateFaqContentStatusParams
>(
  'faq/updateFaqContentStatus',
  async (params: UpdateFaqContentStatusParams) => {
    try {
      const response = await updateFaqContentStatus(params);
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.updateContentStatusFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 更新FAQ分类状态异步action
 */
export const updateFaqCategoryStatusAsync = createAsyncThunk<
  UpdateFaqCategoryStatusResponse,
  UpdateFaqCategoryStatusParams
>(
  'faq/updateFaqCategoryStatus',
  async (params: UpdateFaqCategoryStatusParams) => {
    try {
      const response = await updateFaqCategoryStatus(params);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.msg : i18n.t('faq.updateCategoryStatusFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 结束FAQ列表异步action
 */
export const bEndFaqListAsync = createAsyncThunk<
  BEndFaqListResponse,
  BEndFaqListParams
>(
  'faq/bEndFaqList',
  async (params: BEndFaqListParams) => {
    try {
      const response = await bEndFaqList({
        ...params,
        cateInfo: {
          fcdId: 0,
          pagination: {
            page: 1,
            pageSize: 999
          }
        },
        pagination: {
          page: 1,
          pageSize: 999
        }
      });
      return response;
    } catch (error) {
      const errorMessage = error || i18n.t('faq.getFaqListFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * 获取FAQ详情异步action
 */
export const getFaqDetailAsync = createAsyncThunk<
  GetFaqDetailResponse,
  GetFaqDetailParams
>(
  'faq/getFaqDetail',
  async (params: GetFaqDetailParams) => {
    try {
      const response = await getFaqDetail(params);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.msg : i18n.t('faq.getFaqDetailFailed');
      throw new Error(errorMessage);
    }
  }
);

/**
 * FAQ切片
 */
const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    /**
     * 设置分类列表
     */
    setCategories: (state, action: PayloadAction<FaqCategory[]>) => {
      state.categories = action.payload;
    },
    
    /**
     * 设置文章列表
     */
    setArticles: (state, action: PayloadAction<FaqArticle[]>) => {
      state.articles = action.payload;
    },
    
    /**
     * 重置错误状态
     */
    resetError: (state) => {
      state.error = null;
    },

    /**
     * 重置刷新标记
     */
    resetShouldRefreshList: (state) => {
      state.shouldRefreshList = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // 排序FAQ文章
      .addCase(sortFaqContentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sortFaqContentAsync.fulfilled, (state) => {
        state.loading = false;
        shopify.toast.show(i18n.t('faq.sortCategorySuccess'));
      })
      .addCase(sortFaqContentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.sortContentFailed');
      })
      
      // 排序FAQ分类
      .addCase(sortFaqCategoryAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sortFaqCategoryAsync.fulfilled, (state) => {
        state.loading = false;
        shopify.toast.show(i18n.t('faq.sortCategorySuccess'));
      })
      .addCase(sortFaqCategoryAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.sortCategoryFailed');
      })
      
      // 编辑FAQ分类
      .addCase(editFaqCategoryAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editFaqCategoryAsync.fulfilled, (state, action) => {
        state.loading = false;
        // 如果返回了分类ID，可以在此更新state中的分类
        const fcaId = action.payload?.data?.fcaId;
        if (fcaId) {
          // 此处可以更新categories，但需要前端提供完整的分类数据
          console.log('分类编辑成功，ID:', fcaId);
        }
      })
      .addCase(editFaqCategoryAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.editCategoryFailed');
      })
      
      // 删除FAQ分类
      .addCase(deleteFaqCategoryAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFaqCategoryAsync.fulfilled, (state) => {
        state.loading = false;
        // 可以在此处更新分类列表，移除已删除的分类
        shopify.toast.show(i18n.t('faq.deleteCategorySuccess'));
      })
      .addCase(deleteFaqCategoryAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.deleteCategoryFailed');
      })
      
      // 编辑FAQ文章
      .addCase(editFaqContentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editFaqContentAsync.fulfilled, (state, action) => {
        state.loading = false;
        // 如果返回了文章ID，可以在此更新state中的文章
        const fcdId = action.payload?.data?.fcdId;
        if (fcdId) {
          // 此处可以更新articles，但需要前端提供完整的文章数据
          console.log('文章编辑成功，ID:', fcdId);
        }
      })
      .addCase(editFaqContentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.editContentFailed');
      })
      
      // 删除FAQ文章
      .addCase(deleteFaqContentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFaqContentAsync.fulfilled, (state) => {
        state.loading = false;
        // 可以在此处更新文章列表，移除已删除的文章
        shopify.toast.show(i18n.t('faq.deleteContentSuccess'));
      })
      .addCase(deleteFaqContentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.deleteContentFailed');
      })
      
      // 更新FAQ分类状态
      .addCase(updateFaqCategoryStatusAsync.pending, (state) => {
        // 不设置loading状态，实现无感更新
        // state.loading = true;
        state.error = null;
      })
      .addCase(updateFaqCategoryStatusAsync.fulfilled, (state, action) => {
        // 不设置loading状态，实现无感更新
        // state.loading = false;
        if (shopify?.toast) {
          shopify.toast.show(i18n.t('faq.updateCategoryStatusSuccess'));
        }
        
        // 刷新FAQ列表
        if (action.meta.arg.shopDomain) {
          // 这里需要在外部再次调用bEndFaqListAsync获取最新数据
          // 我们不能在reducer中直接dispatch action，需要在组件中处理
          
          // 可以设置一个标记，让组件知道需要刷新数据
          state.shouldRefreshList = true;
        }
      })
      .addCase(updateFaqCategoryStatusAsync.rejected, (state, action) => {
        // 错误时也不改变loading状态
        // state.loading = false;
        state.error = action.error.message || i18n.t('faq.updateCategoryStatusFailed');
      })
      
      // 更新FAQ文章状态
      .addCase(updateFaqContentStatusAsync.pending, (state) => {
        // 不设置loading状态，实现无感更新
        // state.loading = true;
        state.error = null;
      })
      .addCase(updateFaqContentStatusAsync.fulfilled, (state, action) => {
        // 不设置loading状态，实现无感更新
        // state.loading = false;
        if (shopify?.toast) {
          shopify.toast.show(i18n.t('faq.updateContentStatusSuccess'));
        }
        
        // 刷新FAQ列表
        if (action.meta.arg.shopDomain) {
          // 这里需要在外部再次调用bEndFaqListAsync获取最新数据
          // 我们不能在reducer中直接dispatch action，需要在组件中处理
          
          // 可以设置一个标记，让组件知道需要刷新数据
          state.shouldRefreshList = true;
        }
      })
      .addCase(updateFaqContentStatusAsync.rejected, (state, action) => {
        // 错误时也不改变loading状态
        // state.loading = false;
        state.error = action.error.message || i18n.t('faq.updateContentStatusFailed');
      })
      
      // 结束FAQ列表
      .addCase(bEndFaqListAsync.pending, (state, action) => {
        // 检查是否为无感更新模式
        const isSilent = action.meta.arg.silent === true;
        
        // 仅在非无感模式下设置loading状态
        if (!isSilent) {
          state.loading = true;
        }
        
        state.error = null;
      })
      .addCase(bEndFaqListAsync.fulfilled, (state, action) => {
        // 检查是否为无感更新模式
        const isSilent = action.meta.arg.silent === true;
        
        // 仅在非无感模式下重置loading状态
        if (!isSilent) {
          state.loading = false;
        }
        
        console.log('FAQ列表数据:', action);
        if (action.payload?.contentList) {
          // 保存返回的FAQ列表数据
          state.faqContentList = action.payload.contentList || [];
          state.allCategories = action.payload.cateList || [];
          // 保存分页信息
          if (action.payload.pagination) {
            const { currentPage, pageSize, totalItems, totalPages } = action.payload.pagination;
            state.pagination = {
              currentPage: currentPage || 1,
              pageSize: pageSize || 999,
              totalItems: totalItems || 0,
              totalPages: totalPages || 0
            };
          }
        }
      })
      .addCase(bEndFaqListAsync.rejected, (state, action) => {
        // 检查是否为无感更新模式
        const isSilent = action.meta.arg.silent === true;
        
        // 仅在非无感模式下重置loading状态
        if (!isSilent) {
          state.loading = false;
        }
        
        state.error = action.error.message || i18n.t('faq.endFaqListFailed');
      })
      
      // 获取FAQ详情
      .addCase(getFaqDetailAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFaqDetailAsync.fulfilled, (state, action) => {
        console.log('action.payload', action.payload);
        state.loading = false;
        if (action.payload?.faqContent) {
          // 保存当前文章和所有分类
          state.currentArticle = action.payload.faqContent || null;
          // state.allCategories = action.payload.categoryList || [];
        }
      })
      .addCase(getFaqDetailAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || i18n.t('faq.getFaqDetailFailed');
      });
  }
});

// 导出action creators
export const { 
  setCategories, 
  setArticles, 
  resetError,
  resetShouldRefreshList 
} = faqSlice.actions;

// 导出选择器
// 现在 faqReducer 已经添加到 store 中，这些选择器应该能够正常工作了
export const selectFaqCategories = (state: RootState) => {
  return state.faq?.categories;
};
export const selectFaqArticles = (state: RootState) => {
  return state.faq?.articles;
};
export const selectFaqLoading = (state: RootState) => {
  return state.faq?.loading;
};
export const selectFaqError = (state: RootState) => {
  return state.faq?.error;
};
// 新增的选择器
export const selectFaqContentList = (state: RootState) => {
  return state.faq?.faqContentList;
};
export const selectFaqPagination = (state: RootState) => {
  return state.faq?.pagination;
};
export const selectCurrentArticle = (state: RootState) => {
  return state.faq?.currentArticle;
};
export const selectAllCategories = (state: RootState) => {
  return state.faq?.allCategories;
};

// 导出reducer
export default faqSlice.reducer;
